import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * StickySubNav - Reusable sticky in-page navigation component
 * 
 * @param {Object[]} sections - Array of section objects with id, name, and icon
 * @param {string} topOffset - CSS class for top offset (default: 'top-20')
 * 
 * Usage:
 * <StickySubNav 
 *   sections={[
 *     { id: 'overview', name: 'Overview', icon: 'BookOpen' },
 *     { id: 'features', name: 'Features', icon: 'Star' },
 *   ]}
 * />
 */
const StickySubNav = ({ sections, topOffset = 'top-20' }) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={`sticky ${topOffset} z-40 bg-background backdrop-blur-sm border-b border-border`}>
      <div className="container-executive py-4">
        <nav 
          className="flex flex-wrap items-center justify-center gap-2 md:gap-4" 
          aria-label="Page sections"
        >
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeSection === section.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-muted'
              }`}
              onClick={(e) => scrollToSection(e, section.id)}
            >
              <Icon name={section.icon} size={16} />
              <span className="hidden md:inline">{section.name}</span>
              <span className="md:hidden">{section.name.split(' ')[0]}</span>
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default StickySubNav;
